import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Link } from 'react-style-guide';
import { Thumbnail2d, ThumbnailTypes } from 'roblox-thumbnails';
import { authenticatedUser } from 'header-scripts';
import { getJoinScript } from '../util/gameInstanceUtil';
import serverListService from '../services/serverListService';
import GameInstanceMenu from '../components/GameInstanceMenu';
import serverListConstants from '../constants/serverListConstants';
import RenewPrivateGame from '../components/RenewPrivateGame';
import PrivateServerOwner from '../components/PrivateServerOwner';
import urlConstants from '../constants/urlConstants';
import FriendsInServerLabel from '../components/FriendsInServerLabel';

const { getUserProfileUrl } = urlConstants;
const { resources } = serverListConstants;

function GameInstance({
  id,
  translate,
  cssKey,
  placeId,
  gameServerStatus,
  canManagePlace,
  name,
  vipServerId,
  vipServerSubscription,
  accessCode,
  showSlowGameMessage,
  owner,
  players,
  onShutdownServerSuccess,
  systemFeedbackService,
  isLoading,
  setIsLoading
}) {
  const [subscription, setSubscription] = useState(vipServerSubscription);
  const isOwner = owner?.id === authenticatedUser.id;
  const isPrivateServer = !!vipServerId;

  // If the access code does not exist, the server is inactive.
  const isServerInactive = isPrivateServer && !accessCode;

  const containerClass = classNames('stack-row', `rbx-${cssKey}game-server-item`);
  const menuClass = classNames('link-menu', `rbx-${cssKey}game-server-menu`);
  const serverDetailsClass = classNames('section-left', `rbx-${cssKey}game-server-details'`);
  const serverStatusClass = classNames(
    'text-info rbx-game-status',
    `rbx-${cssKey}game-server-status'`
  );
  const serverAlertClass = `rbx-${cssKey}game-server-alert`;
  const joinButtonClass = classNames(
    'btn-full-width btn-control-xs ',
    `rbx-${cssKey}game-server-join`
  );

  useEffect(() => {
    setSubscription(vipServerSubscription);
  }, [vipServerSubscription]);

  const shutdownServer = () => {
    // This should not be possible due to the disabling of the button,
    // but you can never be too careful :)
    if (isLoading) {
      systemFeedbackService.warning(
        translate(resources.shutdownServerError) || 'Unable to shut down server.'
      );
    }

    setIsLoading(true);
    serverListService
      .shutdownGameInstance(placeId, id, vipServerId)
      .then(
        () => {
          systemFeedbackService.success(
            translate(resources.shutdownServerSuccess) || 'Successfully shut down server.'
          );
          onShutdownServerSuccess();
        },
        () => {
          systemFeedbackService.warning(
            translate(resources.shutdownServerError) || 'Unable to shut down server.'
          );
        }
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <li className={containerClass}>
      <div className='section-header'>
        {name && <span className='font-bold'>{name}</span>}
        <GameInstanceMenu
          {...{
            className: menuClass,
            translate,
            placeId,
            gameId: id,
            vipServerId,
            isOwner,
            canManagePlace,
            shutdownServer,
            isLoading
          }}
        />
      </div>
      <div className={serverDetailsClass}>
        {isPrivateServer && (
          <PrivateServerOwner {...{ ownerUserId: owner.id, ownerName: owner.displayName }} />
        )}
        <div className={serverStatusClass}>
          {gameServerStatus}
          {!isPrivateServer && <FriendsInServerLabel {...{ translate, players }} />}
        </div>
        {showSlowGameMessage && (
          <div className={serverAlertClass}>
            <span className='icon-remove' />
            {translate(resources.slowGameWarning)}
          </div>
        )}
        {isPrivateServer && (
          <RenewPrivateGame
            {...{
              privateServerId: vipServerId,
              isPaymentCancelled: !subscription?.active && subscription?.price > 0,
              isInsufficientFunds: subscription?.hasInsufficientFunds,
              isServerInactive,
              canRenew: subscription?.canRenew && !subscription?.active,
              setSubscription,
              isLoading,
              systemFeedbackService
            }}
          />
        )}
        {!isServerInactive && !subscription?.isExpired && (
          <span data-placeid={placeId}>
            <Button
              className={joinButtonClass}
              onClick={getJoinScript(placeId, { instanceId: id, accessCode })}
              isDisabled={isLoading}>
              {translate(resources.joinServerText)}
            </Button>
          </span>
        )}
      </div>

      <div className='section-right rbx-game-server-players'>
        {players.map(player => (
          <span key={player.playerToken} className='avatar avatar-headshot-sm player-avatar'>
            {player.id == null ? (
              <Thumbnail2d
                type={ThumbnailTypes.avatarHeadshot}
                token={player.playerToken}
                containerClass='avatar-card-image'
              />
            ) : (
              <Link className='avatar-card-link' href={getUserProfileUrl(player.id)}>
                <Thumbnail2d
                  type={ThumbnailTypes.avatarHeadshot}
                  targetId={player.id}
                  containerClass='avatar-card-image'
                  altName={player.displayName}
                />
              </Link>
            )}
          </span>
        ))}
      </div>
    </li>
  );
}

GameInstance.defaultProps = {
  id: null,
  cssKey: '',
  canManagePlace: false,
  name: '',
  vipServerId: 0,
  accessCode: '',
  showSlowGameMessage: false,
  owner: undefined,
  vipServerSubscription: undefined,
  players: []
};

GameInstance.propTypes = {
  id: PropTypes.string,
  translate: PropTypes.func.isRequired,
  cssKey: PropTypes.string,
  placeId: PropTypes.number.isRequired,
  name: PropTypes.string,
  vipServerId: PropTypes.number,
  vipServerSubscription: {
    canRenew: PropTypes.bool.isRequired,
    active: PropTypes.bool.isRequired,
    hasInsufficientFunds: PropTypes.bool.isRequired,
    price: PropTypes.number.isRequired,
    expired: PropTypes.bool.isRequired
  },
  accessCode: PropTypes.string,
  gameServerStatus: PropTypes.string.isRequired,
  canManagePlace: PropTypes.bool,
  showSlowGameMessage: PropTypes.bool,
  owner: {
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired
  },
  players: PropTypes.arrayOf(PropTypes.any),
  onShutdownServerSuccess: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  systemFeedbackService: PropTypes.shape({
    success: PropTypes.func.isRequired,
    warning: PropTypes.func.isRequired
  }).isRequired
};

export default GameInstance;
